
import katex_1 from "katex";
import { zip, singleton as singleton_1, item, isEmpty, mapFold, contains, partition, length, fold as fold_1, filter, map, append } from "../../fable_modules/fable-library-js.5.13.0/List.js";
import { TruthTable, TruthTableCell, CellData, ConstraintSet, CellIO } from "../../Simulator/TruthTableTypes.js";
import { FSharpMap__get_Count, ofList, toList } from "../../fable_modules/fable-library-js.5.13.0/Map.js";
import { truncate, mapIndexed, toList as toList_1, initialize, empty, fold, singleton, append as append_1, map as map_1, collect, delay } from "../../fable_modules/fable-library-js.5.13.0/Seq.js";
import { compare, arrayHash, equalArrays, equals } from "../../fable_modules/fable-library-js.5.13.0/Util.js";
import { convertFastDataToWireData, convertWireDataToFastData, convertIntToWireData } from "../../Simulator/NumberHelpers.js";
import { fromInt32 } from "../../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { FSInterface, FastAlgExp } from "../../Simulator/SimGraphTypes.js";
import { extractViewers, extractViewersFData, extractFastSimulationIOsFData, changeInputBatch } from "../../Simulator/FastSim/FastExtract.js";
import { printf, toFail } from "../../fable_modules/fable-library-js.5.13.0/String.js";
import { instrumentInterval, getTimeMs } from "../../Common/TimeHelpers.js";
import { buildFastSimulationFData } from "../../Simulator/FastSim/FastBuild.js";
import { SimulationData } from "../../Simulator/SimTypes.js";
import { hasRedundancies } from "./TruthTableReduce.js";
import "katex/dist/katex.min.css";


export const katex = katex_1;

/**
 * Wraps SimulationIO and Viewer types in the CellIO DU Type
 */
export function toCellIO(simIOs, viewers) {
    return append(map((io) => (new CellIO(/* SimIO */ 0, [io])), simIOs), map((tupledArg) => {
        const _arg = tupledArg[0];
        return new CellIO(/* Viewer */ 1, [[_arg[0], _arg[1]], tupledArg[1]]);
    }, viewers));
}

/**
 * Converts a Truth Table from a Mapping to a list of its rows.
 * Note that separation of inputs and outputs is lost.
 */
export function tableAsList(tMap) {
    return map((tupledArg) => append(tupledArg[0], tupledArg[1]), toList(tMap));
}

export function product(seq1, seq2) {
    return delay(() => collect((item1) => map_1((item2) => append_1(item2, singleton(item1)), seq2), seq1));
}

/**
 * Find the Cartesian product of n sets, implemented with Sequences
 */
export function productn(s) {
    return fold((r, s_1) => product(s_1, r), delay(() => singleton(empty())), s);
}

export function getConstraintsOnIO(io, constraints) {
    return new ConstraintSet(filter((e) => equals(e.IO, io), constraints.Equalities), filter((i) => equals(i.IO, io), constraints.Inequalities));
}

export function constrainedValuesandLength(_arg) {
    const equ = _arg.Equalities;
    const equValues = map_1((con) => (con.Value | 0), equ);
    const patternInput = fold_1((tupledArg, con_1) => [append_1(initialize(con_1.Range, (x) => ((x + con_1.LowerBound) | 0)), tupledArg[0]), tupledArg[1] + con_1.Range], [empty(), 0], _arg.Inequalities);
    return [append_1(equValues, patternInput[0]), length(equ) + patternInput[1]];
}

/**
 * Find all LHS rows of the Truth Table, limited by input constraints and bit limit
 */
export function tableLHS(inputs, ttt) {
    const rowLimit = ~~Math.pow(2, ttt.BitLimit) | 0;
    const patternInput = partition((io_1) => contains(io_1, ttt.AlgebraIns, {
        Equals: equalArrays,
        GetHashCode: (x_1) => (arrayHash(x_1) | 0),
    }), inputs);
    const numericInputs = patternInput[1];
    const patternInput_1 = mapFold((count, io) => {
        const seqLength = ~~Math.pow(2, io[2]) | 0;
        const matchValue = getConstraintsOnIO(new CellIO(/* SimIO */ 0, [io]), ttt.InputConstraints);
        let matchResult, conSet;
        if (isEmpty(matchValue.Equalities)) {
            if (isEmpty(matchValue.Inequalities)) {
                matchResult = 0;
            }
            else {
                matchResult = 1;
                conSet = matchValue;
            }
        }
        else {
            matchResult = 1;
            conSet = matchValue;
        }
        switch (matchResult) {
            case 0:
                return [initialize(seqLength, (x) => (x | 0)), count * seqLength];
            default: {
                const tupledArg = constrainedValuesandLength(conSet);
                return [tupledArg[0], count * tupledArg[1]];
            }
        }
    }, 1, numericInputs);
    const numericRows = toList_1(map_1((l) => toList_1(mapIndexed((i, n) => {
        const patternInput_2 = item(i, numericInputs);
        return new TruthTableCell(new CellIO(/* SimIO */ 0, [item(i, numericInputs)]), new CellData(/* Bits */ 0, [convertIntToWireData(patternInput_2[2], fromInt32(n))]));
    }, l)), truncate(rowLimit, productn(patternInput_1[0]))));
    const algebraRow = map((io_2) => (new TruthTableCell(new CellIO(/* SimIO */ 0, [io_2]), new CellData(/* Algebra */ 1, [new FastAlgExp(/* SingleTerm */ 0, [io_2])]))), patternInput[0]);
    if (isEmpty(numericInputs)) {
        return [singleton_1(algebraRow), 1];
    }
    else {
        return [map((r) => append(algebraRow, r), numericRows), patternInput_1[1]];
    }
}

/**
 * Feeds the given input combination (LHS Row) to the table's Fast Simulation and extracts the
 * simulated output combination (RHS Row).
 */
export function simulateInputCombination(rowLHS, outputs, simData) {
    changeInputBatch(simData.ClockTickNumber, simData.FastSim, map((cell) => {
        const matchValue = cell.IO;
        const matchValue_1 = cell.Data;
        let matchResult, cid, wd, exp, io, x, y;
        if (matchValue.tag === 0) {
            switch (matchValue_1.tag) {
                case 0: {
                    matchResult = 0;
                    cid = matchValue.fields[0][0];
                    wd = matchValue_1.fields[0];
                    break;
                }
                case 1: {
                    matchResult = 1;
                    exp = matchValue_1.fields[0];
                    io = matchValue.fields[0];
                    break;
                }
                default: {
                    matchResult = 2;
                    x = matchValue;
                    y = matchValue_1;
                }
            }
        }
        else {
            matchResult = 2;
            x = matchValue;
            y = matchValue_1;
        }
        switch (matchResult) {
            case 0:
                return [cid, new FSInterface(/* IData */ 0, [convertWireDataToFastData(wd)])];
            case 1:
                return [io[0], new FSInterface(/* IAlg */ 1, [new FastAlgExp(/* SingleTerm */ 0, [io])])];
            default:
                return toFail(printf("what? CellData from input rows has IO: %A, and Data: %A."))(x)(y);
        }
    }, rowLHS));
    return append(map((tupledArg) => {
        const comp = tupledArg[0];
        const out = tupledArg[1];
        if (out.tag === 1) {
            return new TruthTableCell(new CellIO(/* SimIO */ 0, [comp]), new CellData(/* Algebra */ 1, [out.fields[0]]));
        }
        else {
            return new TruthTableCell(new CellIO(/* SimIO */ 0, [comp]), new CellData(/* Bits */ 0, [convertFastDataToWireData(out.fields[0])]));
        }
    }, extractFastSimulationIOsFData(outputs, simData)), map((tupledArg_1) => {
        const _arg = tupledArg_1[0];
        const w = tupledArg_1[1] | 0;
        const fs = tupledArg_1[2];
        const l = _arg[0];
        const f = _arg[1];
        if (fs.tag === 1) {
            return new TruthTableCell(new CellIO(/* Viewer */ 1, [[l, f], w]), new CellData(/* Algebra */ 1, [fs.fields[0]]));
        }
        else {
            return new TruthTableCell(new CellIO(/* Viewer */ 1, [[l, f], w]), new CellData(/* Bits */ 0, [convertFastDataToWireData(fs.fields[0])]));
        }
    }, extractViewersFData(simData)));
}

/**
 * Create a Truth Table from Simulation Data, taking into account
 * algebraic inputs and input constriants.
 */
export function truthTable(simData, ttType, isRegeneration) {
    let table, tableMap;
    const start = getTimeMs();
    let tableSimData;
    if (isRegeneration) {
        tableSimData = simData;
    }
    else {
        const matchValue = buildFastSimulationFData(2, "", simData.Graph);
        tableSimData = ((matchValue.tag === 0) ? (new SimulationData(matchValue.fields[0], simData.Graph, simData.Inputs, simData.Outputs, simData.IsSynchronous, simData.NumberBase, simData.ClockTickNumber)) : toFail(printf("Error in building fast simulation for Truth Table evaluation")));
    }
    const inputs = map((tuple) => tuple[0], extractFastSimulationIOsFData(simData.Inputs, tableSimData));
    const outputs = map((tuple_1) => tuple_1[0], extractFastSimulationIOsFData(simData.Outputs, tableSimData));
    const viewers = extractViewers(simData);
    const patternInput = tableLHS(inputs, ttType);
    const tCRC = patternInput[1] | 0;
    const lhs = patternInput[0];
    return instrumentInterval("truthTableGeneration", start, (table = ((tableMap = ofList(zip(lhs, map((i) => simulateInputCombination(i, outputs, tableSimData), lhs)), {
        Compare: (x, y) => (compare(x, y) | 0),
    }), new TruthTable(tableMap, tableMap, undefined, tableAsList(tableMap), FSharpMap__get_Count(tableMap) !== tCRC, tCRC, false, tableSimData, toCellIO(append(inputs, outputs), viewers)))), (table.IsTruncated ? true : (length(ttType.AlgebraIns) > 0)) ? table : (new TruthTable(table.TableMap, table.FilteredMap, table.DCMap, table.SortedListRep, table.IsTruncated, table.MaxRowsWithConstraints, hasRedundancies(table), table.TableSimData, table.IOOrder))));
}

